import { LocationInfo } from '@/types/cmsTypes';
import type { SupabaseSceneOrigin } from '@/types/sceneTypes';
import { convertToSceneOrigin } from '@/utils/dataConverters';
import { splitDescription } from '@/modules/library/utils/storyAttributionUtils';
import projectPlaceholder from '@/assets/project-historical.jpg';
import { TEMPLATE_COVER_ORDER } from '@/modules/uiImages/types';
import { v4 as uuid } from 'uuid';
import { copyScene } from '@/infrastructure/clients/scenesApi';
import type { components } from '@/generated/contracts/api-v1';
import { v1, toResult, type V1Result } from '@/infrastructure/api/v1/client';
import { listAll, listAllIn } from '@/infrastructure/api/v1/lists';
import { routeMissing } from '@/infrastructure/api/v1/screenReads';
import { sceneToLegacy } from '@/infrastructure/api/v1/mappers/scenes';
import { storyToLegacy } from '@/infrastructure/api/v1/mappers/stories';
import { imageToLegacy } from '@/infrastructure/api/v1/mappers/images';
import { projectEnsureBody } from '@/infrastructure/api/v1/mappers/projects';
import { CONSUMED_TEMPLATES_CATEGORY, CONSUMED_TEMPLATES_PROJECT_NAME } from './templateProjects';
import type { ExistingCopyLookup, GalleryTemplate } from './types';

type TemplateGallery = components['schemas']['TemplateGalleryDto'];

const GALLERY_IMAGE_CATEGORIES = ['SceneLandscape', 'TemplateLandscape', 'SceneVideo'];

/**
 * The gallery read part by part, as getTemplateGallery answers it, for a backend without that read (rolled back to
 * before it): the accepted templates (listAcceptedTemplates), their template versions (listScenes), those versions'
 * stories (listStories) and the stories' gallery images, newest first (listImages).
 */
async function readGalleryPartByPart(): Promise<V1Result<TemplateGallery>> {
  const accepted = await listAll(page => v1.GET('/api/v1/accepted-templates', { params: { query: { ...page } } }));
  if (accepted.error) return { data: null, error: accepted.error };
  const acceptedOriginIds = [...new Set(accepted.data.map(submission => submission.sceneOriginUniqueId))];
  if (acceptedOriginIds.length === 0) return { data: { items: [] }, error: null };

  const scenes = await listAllIn(acceptedOriginIds, (storyIds, page) =>
    v1.GET('/api/v1/scenes', { params: { query: { storyUniqueId: storyIds, status: ['template'], ...page } } }));
  if (scenes.error) return { data: null, error: scenes.error };
  const storyIds = [...new Set(scenes.data.flatMap(scene => (scene.storyUniqueId ? [scene.storyUniqueId] : [])))];
  if (storyIds.length === 0) return { data: { items: [] }, error: null };

  const [stories, images] = await Promise.all([
    listAllIn(storyIds, (ids, page) => v1.GET('/api/v1/stories', { params: { query: { uniqueId: ids, ...page } } })),
    listAllIn(storyIds, (ids, page) => v1.GET('/api/v1/images', {
      params: { query: { sceneOriginUniqueId: ids, category: GALLERY_IMAGE_CATEGORIES, sort: '-createdAt', ...page } },
    }), { compare: (a, b) => b.createdAt.localeCompare(a.createdAt) }),
  ]);
  if (stories.error || images.error) return { data: null, error: stories.error ?? images.error };
  return {
    data: {
      items: stories.data.map(story => ({
        story,
        templates: accepted.data.filter(submission => submission.sceneOriginUniqueId === story.uniqueId),
        scenes: scenes.data.filter(scene => scene.storyUniqueId === story.uniqueId),
        images: images.data.filter(image => image.sceneOriginUniqueId === story.uniqueId),
      })),
    },
    error: null,
  };
}

export class TemplateScenesService {
  /**
   * The gallery's templates, in one read (getTemplateGallery): each template story accepted into the gallery, with its
   * template version and its gallery images, as listAcceptedTemplates, listScenes, listStories and listImages show them.
   * A backend without that read is read part by part (readGalleryPartByPart).
   */
  async fetchTemplateScenes(): Promise<GalleryTemplate[]> {
    try {
      const read = await toResult(v1.GET('/api/v1/template-gallery'));
      const { data: gallery, error: galleryError } = routeMissing(read.error) ? await readGalleryPartByPart() : read;

      if (galleryError) {
        console.error('Error fetching the template gallery:', galleryError);
        throw new Error('Failed to fetch the template gallery');
      }

      // Only templates with an accepted submission are in the gallery (admin and creator both have a template_submissions row)
      const ratingByOriginId = new Map<string, number>();
      const acceptedAtByOriginId = new Map<string, string>();
      for (const submission of gallery.items.flatMap(item => item.templates)) {
        const acceptedAt = submission.acceptedAt;
        const latest = acceptedAtByOriginId.get(submission.sceneOriginUniqueId);
        if (acceptedAt && (!latest || acceptedAt > latest)) acceptedAtByOriginId.set(submission.sceneOriginUniqueId, acceptedAt);

        if (submission.rating == null) continue;
        const current = ratingByOriginId.get(submission.sceneOriginUniqueId);
        if (current === undefined || submission.rating > current) {
          ratingByOriginId.set(submission.sceneOriginUniqueId, submission.rating);
        }
      }

      // Each template's version with status 'template', and its story
      const templateInstances = gallery.items.flatMap(item => item.scenes).map(sceneToLegacy);
      const sceneOriginIds = gallery.items.map(item => item.story.uniqueId);
      const sceneOrigins = gallery.items.map(item => storyToLegacy(item.story) as unknown as SupabaseSceneOrigin);
      if (sceneOrigins.length === 0) {
        return [];
      }

      // The template stories' gallery images, newest first
      const imageDtos = gallery.items.flatMap(item => item.images);
      const uiImagesRows = imageDtos.map(imageToLegacy);
      const uiImages = (uiImagesRows || []) as Array<{
        scene_origin_id?: string;
        image_category?: string;
        image_cropped_url?: string;
        image_original_url?: string;
        image_order_number?: number;
      }>;

      // Group by scene_origin_id and category (data is ordered by created_at desc, so first per group is latest)
      const imagesBySceneOrigin: Record<
        string,
        {
          heroImage: string;
          templateLandscapeImages: string[];
          videoUrl?: string;
        }
      > = {};

      for (const sceneOriginId of sceneOriginIds) {
        imagesBySceneOrigin[String(sceneOriginId)] = {
          heroImage: projectPlaceholder,
          templateLandscapeImages: [],
          videoUrl: undefined
        };
      }

      const displayUrl = (img?: { image_cropped_url?: string; image_original_url?: string }): string =>
        img ? img.image_cropped_url || img.image_original_url || '' : '';

      // uiImages is ordered by created_at desc, so the first match per category is the
      // latest. The hero is the template's own cover when it has one, else the story's.
      for (const originId of sceneOriginIds) {
        const entry = imagesBySceneOrigin[String(originId)];
        const forOrigin = uiImages.filter((img) => img.scene_origin_id === originId);
        const landscapeForOrigin = forOrigin.filter(
          (img) => img.image_category === 'TemplateLandscape'
        );

        const ownCover = landscapeForOrigin.find(
          (img) => img.image_order_number === TEMPLATE_COVER_ORDER && displayUrl(img) !== ''
        );
        const storyCover = forOrigin.find(
          (img) => img.image_category === 'SceneLandscape' && displayUrl(img) !== ''
        );
        entry.heroImage = displayUrl(ownCover) || displayUrl(storyCover) || projectPlaceholder;

        entry.templateLandscapeImages = landscapeForOrigin
          .filter((img) => img.image_order_number !== TEMPLATE_COVER_ORDER)
          .sort((a, b) => (a.image_order_number || 0) - (b.image_order_number || 0))
          .map(displayUrl)
          .filter((u) => u !== '');

        const video = forOrigin.find(
          (img) => img.image_category === 'SceneVideo' && img.image_original_url
        );
        entry.videoUrl = video?.image_original_url;
      }

      const imagesMap = new Map(
        Object.entries(imagesBySceneOrigin).map(([sceneOriginId, data]) => [sceneOriginId, data])
      );

      // Step 5: Convert to TemplateScene format
      const templateScenes: GalleryTemplate[] = sceneOrigins.map((supabaseSceneOrigin) => {
        const sceneOrigin = convertToSceneOrigin(supabaseSceneOrigin);
        const images = imagesMap.get(String(sceneOrigin.sceneOriginId));
        
        // Find the corresponding scene instance to get duration
        const sceneInstance = templateInstances.find(
          inst => inst.scene_origin_id === sceneOrigin.sceneOriginId
        );
        const durationMs = sceneInstance?.scene_instance_duration_ms;
        const duration = durationMs ? Math.round(durationMs / 60000) : undefined; // Convert to minutes

        const originalTemplateSceneInstanceId = sceneInstance?.scene_instance_id ?? undefined;

        let locationInfo: LocationInfo | undefined;
        if (sceneOrigin.sceneOriginLocationInfo?.coordinates) {
          locationInfo = {
            latitude: sceneOrigin.sceneOriginLocationInfo.coordinates.lat ?? 0,
            longitude: sceneOrigin.sceneOriginLocationInfo.coordinates.lng ?? 0,
            address: sceneOrigin.sceneOriginLocationInfo.address
          };
        }

        const category = sceneOrigin.sceneOriginCategory || 'General';
        const tags = sceneOrigin.sceneOriginTags || [];
        const features = sceneOrigin.sceneOriginFeatures || [];
        const learnings = sceneOrigin.sceneOriginLearnings || '';
        const difficulty = sceneOrigin.sceneOriginDifficulty || 'beginner';

        return {
          id: sceneOrigin.sceneOriginId,
          title: sceneOrigin.sceneOriginName || 'Untitled Template',
          description: splitDescription(sceneOrigin.sceneOriginDescription || '').userText || 'No description available.',
          heroImage: images?.heroImage || projectPlaceholder,
          templateLandscapeImages: images?.templateLandscapeImages || [],
          videoUrl: images?.videoUrl,
          category: category,
          tags: tags,
          features: features,
          learnings: learnings,
          duration: duration || 0,
          difficulty: difficulty as 'beginner' | 'intermediate' | 'advanced',
          locationInfo: locationInfo,
          creatorUserId: sceneOrigin.userId,
          originalTemplateSceneInstanceId,
          rating: ratingByOriginId.get(sceneOrigin.sceneOriginId) ?? null,
          acceptedAt: acceptedAtByOriginId.get(sceneOrigin.sceneOriginId) ?? null
        };
      });

      templateScenes.sort((a, b) => (b.rating ?? 0) - (a.rating ?? 0));

      return templateScenes;
    } catch (error) {
      console.error('Error fetching template scenes:', error);
      throw error;
    }
  }

  /**
   * Get the scene instance ID for a template scene (for admin users)
   */
  async getTemplateSceneInstanceId(templateSceneOriginId: string): Promise<string | null> {
    try {
      const { data, error } = await toResult(v1.GET('/api/v1/scenes', {
        params: { query: { storyUniqueId: [templateSceneOriginId], status: ['template'], limit: 1 } },
      }));
      const sceneInstance = data?.items[0];

      if (error || !sceneInstance) {
        console.error('Error fetching template scene instance:', error);
        return null;
      }

      return sceneInstance.uniqueId;
    } catch (error) {
      console.error('Error getting template scene instance ID:', error);
      return null;
    }
  }

  // Anything short of a certain, discardable copy counts as none, so Open never offers to discard a story still needed
  async findExistingTemplateCopy(userId: string, templateStoryId: string): Promise<ExistingCopyLookup> {
    const { data: templateScenes, error: templateError } = await toResult(v1.GET('/api/v1/scenes', {
      params: { query: { storyUniqueId: [templateStoryId], status: ['template'], limit: 1 } },
    }));
    if (templateError) return { kind: 'error' };
    const sceneTypeId = templateScenes.items[0]?.sceneTypeUniqueId;
    if (!sceneTypeId) return { kind: 'none' };

    const { data: projects, error: projectsError } = await listAll(page =>
      v1.GET('/api/v1/projects', { params: { query: { userUniqueId: userId, category: [CONSUMED_TEMPLATES_CATEGORY], ...page } } }),
    );
    if (projectsError) return { kind: 'error' };
    const myTemplates = new Set(
      projects.filter(p => p.category === CONSUMED_TEMPLATES_CATEGORY && p.userUniqueId === userId).map(p => p.uniqueId),
    );
    if (myTemplates.size === 0) return { kind: 'none' };

    const { data: matches, error: matchesError } = await listAll(page =>
      v1.GET('/api/v1/scenes', {
        params: { query: { userUniqueId: userId, sceneTypeUniqueId: sceneTypeId, projectUniqueId: [...myTemplates], status: ['draft'], ...page } },
      }),
    );
    if (matchesError) return { kind: 'error' };
    const candidates = matches.filter(scene =>
      scene.status === 'draft' &&
      scene.userUniqueId === userId &&
      myTemplates.has(scene.projectUniqueId) &&
      !!scene.storyUniqueId &&
      scene.storyUniqueId !== templateStoryId,
    );
    if (candidates.length === 0) return { kind: 'none' };

    // Start Fresh discards the whole story, so every version of it has to be a draft copy in My Templates
    const { data: versions, error: versionsError } = await listAllIn(
      candidates.map(scene => scene.storyUniqueId as string),
      (storyIds, page) => v1.GET('/api/v1/scenes', { params: { query: { storyUniqueId: storyIds, ...page } } }),
    );
    if (versionsError) return { kind: 'error' };
    const unsafeStories = new Set(
      versions
        .filter(v => v.status !== 'draft' || v.userUniqueId !== userId || !myTemplates.has(v.projectUniqueId))
        .map(v => v.storyUniqueId),
    );

    const copy = candidates
      .filter(scene => !unsafeStories.has(scene.storyUniqueId))
      .sort((a, b) => (b.updatedAt ?? b.createdAt).localeCompare(a.updatedAt ?? a.createdAt))[0];
    if (!copy?.storyUniqueId) return { kind: 'none' };

    return {
      kind: 'found',
      copy: {
        sceneUniqueId: copy.uniqueId,
        storyUniqueId: copy.storyUniqueId,
        projectUniqueId: copy.projectUniqueId,
        name: copy.displayName ?? null,
      },
    };
  }

  /**
   * Find or create the "My Templates" project, which holds copies the user made
   * FROM the gallery. Returns the project_instance_id
   */
  async findOrCreateTemplatesProject(): Promise<string | null> {
    // ensureProject finds the signed-in user's project in the category, or creates it in their org
    const { data: project, error } = await toResult(v1.POST('/api/v1/projects/ensure', {
      body: projectEnsureBody({
        project_instance_id: uuid(),
        project_instance_category: CONSUMED_TEMPLATES_CATEGORY,
        project_instance_display_name: CONSUMED_TEMPLATES_PROJECT_NAME,
      }),
    }));

    if (error || !project) {
      console.error(`Error finding or creating ${CONSUMED_TEMPLATES_CATEGORY} project:`, error);
      return null;
    }

    return project.uniqueId;
  }

  /**
   * Copy template scene into an existing project
   * Returns the new scene instance ID
   */
  async copyTemplateSceneToProject(
    templateSceneOriginId: string,
    targetProjectInstanceId: string,
  ): Promise<string> {
    try {
      // Get the template scene instance ID
      const sceneInstanceId = await this.getTemplateSceneInstanceId(templateSceneOriginId);
      if (!sceneInstanceId) {
        throw new Error('This template could not be found. Please try again.');
      }

      const copy = await copyScene(sceneInstanceId, { projectId: targetProjectInstanceId });
      const newSceneInstanceId = copy.uniqueId;
      console.log('Successfully copied template scene to project:', newSceneInstanceId);

      return newSceneInstanceId;
    } catch (error) {
      console.error('Error copying template scene to project:', error);
      throw error;
    }
  }
}
